import { NotificationState } from '@webinex/wispo';
import { useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Notification } from '@app/notifications/api';
import { Link } from 'react-router-dom';
import { useUserContextSelector } from '@app/main/utils';

const notificationLinkOverrides: Partial<
  Record<Notification['type'], { patient?: string; provider?: string }>
> = {};

export function useLocalizeNotification() {
  const [t, { exists }] = useTranslation();
  const isPatient = useUserContextSelector((x) => x.hasRole('PATIENT'));

  return useCallback(
    (notification: NotificationState<Notification>) => {
      const { type } = notification;
      const prefix = `notifications.${type}`;
      const subjectKey = `${prefix}.subject`;
      const bodyKey = `${prefix}.body`;
      const override = notificationLinkOverrides[type]?.[isPatient ? 'patient' : 'provider'];
      const link = override ?? t(`${prefix}.link`, { notification });

      switch (type) {
        case 'DEMO':
          return {
            ...notification,
            subject: notification.data.subject,
            body: `${notification.data.sentBy.firstName} ${notification.data.sentBy.lastName} sent you a message '${notification.data.body}'`,
          };

        default:
          return {
            ...notification,
            subject: exists(subjectKey) ? t(subjectKey, { notification })! : t('notifications.unknown')!,
            body: exists(bodyKey) ? (
              <Trans i18nKey={bodyKey} values={{ notification }}>
                <Link className="underline" to={link} />
              </Trans>
            ) : (
              ''
            ),
          };
      }
    },
    [isPatient, exists, t],
  );
}

export function useLocalizedNotification(notification: NotificationState<Notification>) {
  const localize = useLocalizeNotification();
  return useMemo(() => localize(notification), [localize, notification]);
}
